import { useEffect, useState } from 'react'
import {
  TIER_FEATURES,
  type SubscriptionFeatures,
  type SubscriptionTier,
} from '@busswerks/shared'
import {
  DEV_TIER_OVERRIDE_KEY,
  isDevTierOverrideAllowed,
  readDevTierOverride,
} from '@/lib/devTierOverride'
import { useAuth } from './useAuth'

// Re-export so existing call sites (DevTierPanel, tests) that imported
// these from the hook file keep working after the move to
// `lib/devTierOverride.ts`.
export { DEV_TIER_OVERRIDE_KEY, isDevTierOverrideAllowed }

/**
 * Single lens that every tier-gated UI should read from. Returns the
 * server's real features by default, but in DEV a localStorage
 * override (written by DevTierPanel) merges the chosen tier's features
 * on top so we can exercise gating paths without touching prod.
 *
 * Re-reads when the `storage` event fires so toggling the panel
 * updates every component within the same tab instantly.
 */
export function useEffectiveFeatures(): {
  features: SubscriptionFeatures | null
  tier: SubscriptionTier | null
  isOverridden: boolean
} {
  const { user } = useAuth()
  const [override, setOverride] = useState<SubscriptionTier | null>(() =>
    readDevTierOverride(),
  )

  useEffect(() => {
    if (!isDevTierOverrideAllowed()) return
    const handler = (e: StorageEvent) => {
      if (e.key !== null && e.key !== DEV_TIER_OVERRIDE_KEY) return
      setOverride(readDevTierOverride())
    }
    // Custom in-tab event from DevTierPanel — `storage` only fires
    // across tabs, so we also listen on the window for local pokes.
    const local = () => setOverride(readDevTierOverride())
    window.addEventListener('storage', handler)
    window.addEventListener('busswerks:dev-tier-changed', local)
    return () => {
      window.removeEventListener('storage', handler)
      window.removeEventListener('busswerks:dev-tier-changed', local)
    }
  }, [])

  if (override) {
    return {
      features: TIER_FEATURES[override],
      tier: override,
      isOverridden: true,
    }
  }
  return {
    features: user?.features ?? null,
    tier: (user?.tier as SubscriptionTier | undefined) ?? null,
    isOverridden: false,
  }
}
